import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";

import {
  AddRounded,
  CheckCircleRounded,
  ContentCopyRounded,
  DeleteOutlineRounded,
  EditRounded,
  GroupsRounded,
  OpenInNewRounded,
  RefreshRounded,
  SendRounded,
  VisibilityRounded,
  BlockRounded,
} from "@mui/icons-material";

import axiosInstance from "../axiosInstance";

import {
  ADMIN_COLORS,
  AdminHeader,
  AdminPage,
  ErrorAlert,
  LoadingState,
  ROLE_OPTIONS,
  RoleChip,
  SearchField,
  SectionCard,
  StatCard,
  copyToClipboard,
  dangerButtonSx,
  fieldSx,
  normalizeText,
  primaryButtonSx,
  secondaryButtonSx,
  tableHeaderSx,
} from "../components/admin/AdminUI";

const initialForm = {
  email: "",
  role: "ROLE_PREMIUMPRO",
  empresa: "",
  ubicacion: "",
  sector: "",
  numEmpleados: "",
  emailSoporte: "",
};

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("es-ES", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return "-";
  }
};

const isUserActive = (user) => Number(user?.validation) === 1;

const StatusChip = ({ user }) => {
  const active = isUserActive(user);

  return (
    <Chip
      size="small"
      icon={active ? <CheckCircleRounded /> : <BlockRounded />}
      label={active ? "Activo" : "Baja"}
      sx={{
        height: 27,
        fontWeight: 850,
        fontSize: ".72rem",
        borderRadius: "999px",
        color: active ? ADMIN_COLORS.success || "#14A67A" : ADMIN_COLORS.danger,
        bgcolor: active
          ? ADMIN_COLORS.successSoft || "#EAFBF6"
          : ADMIN_COLORS.dangerSoft || "#FFEBEE",
        border: `1px solid ${
          active
            ? `${ADMIN_COLORS.success || "#14A67A"}33`
            : `${ADMIN_COLORS.danger}33`
        }`,
        "& .MuiChip-icon": {
          fontSize: 16,
          color: active ? ADMIN_COLORS.success || "#14A67A" : ADMIN_COLORS.danger,
        },
      }}
    />
  );
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(initialForm);
  const [selectedUser, setSelectedUser] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [invitation, setInvitation] = useState(null);

  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [validationTarget, setValidationTarget] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await axiosInstance.get("/admin/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err?.response?.data?.message || "No se pudieron cargar los usuarios."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const counts = useMemo(() => {
    return users.reduce(
      (acc, user) => {
        acc.total += 1;

        if (isUserActive(user)) acc.active += 1;
        else acc.inactive += 1;

        acc[user.role] = (acc[user.role] || 0) + 1;

        return acc;
      },
      {
        total: 0,
        active: 0,
        inactive: 0,
      }
    );
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = normalizeText(search);

    return users.filter((user) => {
      const matchRole = !roleFilter || user.role === roleFilter;

      const matchStatus =
        statusFilter === ""
          ? true
          : statusFilter === "active"
            ? isUserActive(user)
            : !isUserActive(user);

      const matchSearch =
        !term ||
        normalizeText(
          `${user.email || ""} ${user.empresa || ""} ${user.sector || ""} ${
            user.ubicacion || ""
          }`
        ).includes(term);

      return matchRole && matchStatus && matchSearch;
    });
  }, [users, roleFilter, statusFilter, search]);

  const openDialog = (nextMode, user = null) => {
    setMode(nextMode);
    setSelectedUser(user);
    setInvitation(null);

    setForm(
      nextMode === "create"
        ? initialForm
        : {
            email: user?.email || "",
            role: user?.role || "ROLE_PREMIUMPRO",
            empresa: user?.empresa || "",
            ubicacion: user?.ubicacion || "",
            sector: user?.sector || "",
            numEmpleados: user?.numEmpleados ?? "",
            emailSoporte: user?.emailSoporte || "",
          }
    );

    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setInvitation(null);
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const submit = async () => {
    if (mode === "view") return closeDialog();

    setSaving(true);
    setError("");

    try {
      const payload = {
        ...form,
        email: form.email.trim().toLowerCase(),
        emailSoporte: form.emailSoporte?.trim().toLowerCase() || undefined,
        numEmpleados:
          form.numEmpleados === "" ? undefined : Number(form.numEmpleados),
      };

      if (mode === "create") {
        const { data } = await axiosInstance.post("/admin/users/invite", payload);

        setInvitation(data?.invitationUrl ? data : null);

        setSnackbar({
          open: true,
          message: data?.message || "Usuario creado correctamente.",
          severity: data?.mail?.sent === false ? "warning" : "success",
        });

        await loadUsers();

        if (!data?.invitationUrl) closeDialog();
      } else {
        const { data } = await axiosInstance.put(
          `/admin/users/update/${selectedUser._id}`,
          payload
        );

        setSnackbar({
          open: true,
          message: data?.message || "Usuario actualizado correctamente.",
          severity: "success",
        });

        await loadUsers();
        closeDialog();
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "No se pudo guardar el usuario.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const resendInvitation = async (user) => {
    setSaving(true);

    try {
      const { data } = await axiosInstance.post(
        `/admin/users/${user._id}/resend-invitation`
      );

      setInvitation(data?.invitationUrl ? data : null);

      setSnackbar({
        open: true,
        message: data?.message || "Invitación reenviada.",
        severity: data?.mail?.sent === false ? "warning" : "success",
      });

      if (data?.invitationUrl) {
        openDialog("view", data.user || user);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message || "No se pudo reenviar la invitación.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      await axiosInstance.delete(`/admin/users/delete/${deleteTarget._id}`);

      setSnackbar({
        open: true,
        message: "Usuario eliminado correctamente.",
        severity: "info",
      });

      await loadUsers();
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message || "No se pudo eliminar el usuario.",
        severity: "error",
      });
    } finally {
      setDeleteTarget(null);
    }
  };

  const openValidationDialog = (user, nextValidation) => {
    setValidationTarget({
      user,
      nextValidation,
    });
  };

  const closeValidationDialog = () => {
    setValidationTarget(null);
  };

  const confirmValidationChange = async () => {
    const user = validationTarget?.user;
    const nextValidation = validationTarget?.nextValidation;

    if (!user?._id) return;

    setUpdatingUserId(user._id);

    try {
      const { data } = await axiosInstance.patch(
        `/admin/users/${user._id}/validation`,
        {
          validation: nextValidation,
        }
      );

      const updatedUser = data?.user;

      setUsers((prev) =>
        prev.map((item) =>
          item._id === user._id
            ? {
                ...item,
                validation: updatedUser?.validation ?? nextValidation,
              }
            : item
        )
      );

      setSnackbar({
        open: true,
        message:
          data?.message ||
          (nextValidation === 1
            ? "Usuario activado correctamente."
            : "Usuario dado de baja correctamente."),
        severity: nextValidation === 1 ? "success" : "warning",
      });

      closeValidationDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          "No se pudo actualizar el estado del usuario.",
        severity: "error",
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (loading) return <LoadingState text="Cargando usuarios..." />;

  return (
    <AdminPage>
      <AdminHeader
        backTo="/admin-dashboard"
        eyebrow="Administración de usuarios"
        title="Usuarios y accesos"
        subtitle="Crea cuentas por invitación, gestiona perfiles, activa usuarios y da de baja accesos cuando sea necesario."
        icon={<GroupsRounded />}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshRounded />}
              onClick={loadUsers}
              sx={secondaryButtonSx}
            >
              Actualizar
            </Button>

            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => openDialog("create")}
              sx={primaryButtonSx}
            >
              Invitar usuario
            </Button>
          </>
        }
      />

      <ErrorAlert error={error} />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total usuarios"
            value={counts.total}
            icon={<GroupsRounded />}
            color={ADMIN_COLORS.textStrong}
            bg="#F8FAFC"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Usuarios activos"
            value={counts.active}
            icon={<CheckCircleRounded />}
            color={ADMIN_COLORS.success || "#14A67A"}
            bg={ADMIN_COLORS.successSoft || "#EAFBF6"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Usuarios de baja"
            value={counts.inactive}
            icon={<BlockRounded />}
            color={ADMIN_COLORS.danger}
            bg={ADMIN_COLORS.dangerSoft || "#FFEBEE"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Premium Pro"
            value={counts.ROLE_PREMIUMPRO || 0}
            icon={
              ROLE_OPTIONS.find((role) => role.value === "ROLE_PREMIUMPRO")
                ?.icon || <GroupsRounded />
            }
            color={
              ROLE_OPTIONS.find((role) => role.value === "ROLE_PREMIUMPRO")
                ?.color || ADMIN_COLORS.primary
            }
            bg={
              ROLE_OPTIONS.find((role) => role.value === "ROLE_PREMIUMPRO")
                ?.bg || "#EEF5FF"
            }
          />
        </Grid>
      </Grid>

      <SectionCard
        title="Directorio de usuarios"
        subtitle="Filtra, edita, reenvía invitaciones o cambia el estado de acceso."
        action={
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Buscar por email, empresa o sector..."
            />

            <FormControl size="small" sx={{ minWidth: 170, ...fieldSx }}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">De baja</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 190, ...fieldSx }}>
              <InputLabel>Rol</InputLabel>
              <Select
                label="Rol"
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        }
      >
        <TableContainer>
          <Table>
            <TableHead sx={tableHeaderSx}>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Última invitación</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredUsers.map((user) => {
                const active = isUserActive(user);
                const nextValidation = active ? 0 : 1;

                return (
                  <TableRow
                    key={user._id}
                    hover
                    sx={{
                      "& td": {
                        borderColor: ADMIN_COLORS.border,
                      },
                      opacity: active ? 1 : 0.72,
                    }}
                  >
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography
                          sx={{
                            fontWeight: 850,
                            color: ADMIN_COLORS.textStrong,
                          }}
                        >
                          {user.email}
                        </Typography>

                        <Typography
                          sx={{
                            color: ADMIN_COLORS.muted,
                            fontSize: ".75rem",
                          }}
                        >
                          {user.emailSoporte && user.emailSoporte !== user.email
                            ? `Soporte: ${user.emailSoporte}`
                            : ""}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <RoleChip role={user.role} />
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StatusChip user={user} />

                        <Tooltip
                          title={
                            active
                              ? "Dar de baja el usuario"
                              : "Activar el usuario"
                          }
                        >
                          <Switch
                            checked={active}
                            disabled={updatingUserId === user._id}
                            onChange={() =>
                              openValidationDialog(user, nextValidation)
                            }
                            size="small"
                            color="primary"
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 750,
                          color: ADMIN_COLORS.text,
                        }}
                      >
                        {user.empresa || "-"}
                      </Typography>

                      <Typography
                        sx={{
                          color: ADMIN_COLORS.muted,
                          fontSize: ".75rem",
                        }}
                      >
                        {[user.sector, user.ubicacion]
                          .filter(Boolean)
                          .join(" · ") || ""}
                      </Typography>
                    </TableCell>

                    <TableCell>{formatDate(user.invitationSentAt)}</TableCell>

                    <TableCell align="right">
                      <Tooltip title="Ver">
                        <IconButton onClick={() => openDialog("view", user)}>
                          <VisibilityRounded />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Editar">
                        <IconButton onClick={() => openDialog("edit", user)}>
                          <EditRounded />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Reenviar invitación">
                        <IconButton
                          onClick={() => resendInvitation(user)}
                          disabled={saving}
                        >
                          <SendRounded />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Eliminar">
                        <IconButton
                          onClick={() => setDeleteTarget(user)}
                          sx={{ color: ADMIN_COLORS.danger }}
                        >
                          <DeleteOutlineRounded />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {!filteredUsers.length ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              No hay usuarios para los filtros seleccionados.
            </Alert>
          </Box>
        ) : null}
      </SectionCard>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "24px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography
            sx={{
              fontWeight: 950,
              color: ADMIN_COLORS.textStrong,
              fontSize: "1.25rem",
            }}
          >
            {mode === "create"
              ? "Invitar nuevo usuario"
              : mode === "edit"
                ? "Editar usuario"
                : "Detalle de usuario"}
          </Typography>

          <Typography
            sx={{
              color: ADMIN_COLORS.muted,
              fontSize: ".85rem",
              mt: 0.4,
            }}
          >
            {mode === "create"
              ? "El usuario recibirá un enlace para configurar su contraseña."
              : "Información de acceso, estado y datos organizativos."}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: ADMIN_COLORS.surfaceSoft }}>
          {mode !== "create" && selectedUser ? (
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 1.4,
                borderRadius: "16px",
                border: `1px solid ${ADMIN_COLORS.border}`,
                bgcolor: "#FFFFFF",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: ADMIN_COLORS.textStrong,
                    }}
                  >
                    Estado de acceso
                  </Typography>

                  <Typography
                    sx={{
                      color: ADMIN_COLORS.muted,
                      fontSize: ".78rem",
                      mt: 0.2,
                    }}
                  >
                    Controla si este usuario puede acceder a la plataforma.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusChip user={selectedUser} />

                  <Switch
                    checked={isUserActive(selectedUser)}
                    disabled={updatingUserId === selectedUser._id}
                    onChange={() =>
                      openValidationDialog(
                        selectedUser,
                        isUserActive(selectedUser) ? 0 : 1
                      )
                    }
                    color="primary"
                  />
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          <Grid container spacing={1.5} sx={{ mt: 0.1 }}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Email de acceso"
                value={form.email}
                onChange={handleChange("email")}
                disabled={mode !== "create"}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <FormControl fullWidth sx={fieldSx} disabled={mode === "view"}>
                <InputLabel>Rol</InputLabel>
                <Select
                  label="Rol"
                  value={form.role}
                  onChange={handleChange("role")}
                >
                  {ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Empresa"
                value={form.empresa}
                onChange={handleChange("empresa")}
                disabled={mode === "view"}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sector"
                value={form.sector}
                onChange={handleChange("sector")}
                disabled={mode === "view"}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ubicación"
                value={form.ubicacion}
                onChange={handleChange("ubicacion")}
                disabled={mode === "view"}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nº empleados"
                type="number"
                value={form.numEmpleados}
                onChange={handleChange("numEmpleados")}
                disabled={mode === "view"}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email de recepción de invitación (opcional)"
                value={form.emailSoporte}
                onChange={handleChange("emailSoporte")}
                disabled={mode === "view"}
                helperText="Déjalo vacío para enviar la invitación al email de acceso."
                sx={fieldSx}
              />
            </Grid>
          </Grid>

          {invitation?.invitationUrl ? (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "18px",
                border: `1px solid ${ADMIN_COLORS.warning}33`,
                bgcolor: ADMIN_COLORS.warningSoft,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: ADMIN_COLORS.textStrong,
                    }}
                  >
                    Enlace de configuración generado
                  </Typography>

                  <Typography
                    sx={{
                      color: ADMIN_COLORS.muted,
                      fontSize: ".78rem",
                      wordBreak: "break-all",
                    }}
                  >
                    {invitation.invitationUrl}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<ContentCopyRounded />}
                  sx={secondaryButtonSx}
                  onClick={() =>
                    copyToClipboard(invitation.invitationUrl, () =>
                      setSnackbar({
                        open: true,
                        message: "Enlace copiado.",
                        severity: "success",
                      })
                    )
                  }
                >
                  Copiar
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<OpenInNewRounded />}
                  sx={secondaryButtonSx}
                  onClick={() => window.open(invitation.invitationUrl, "_blank")}
                >
                  Abrir
                </Button>
              </Stack>
            </Paper>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} sx={secondaryButtonSx}>
            Cerrar
          </Button>

          {mode !== "view" ? (
            <Button
              onClick={submit}
              disabled={saving}
              variant="contained"
              sx={primaryButtonSx}
            >
              {saving
                ? "Guardando..."
                : mode === "create"
                  ? "Crear e invitar"
                  : "Guardar cambios"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(validationTarget)}
        onClose={closeValidationDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "22px",
          },
        }}
      >
        <DialogTitle>
          {validationTarget?.nextValidation === 1
            ? "Activar usuario"
            : "Dar de baja usuario"}
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              color: ADMIN_COLORS.text,
              lineHeight: 1.6,
            }}
          >
            {validationTarget?.nextValidation === 1
              ? "El usuario volverá a tener acceso a la plataforma."
              : "El usuario quedará dado de baja y no podrá acceder mientras su estado esté desactivado."}
          </Typography>

          {validationTarget?.user?.email ? (
            <Typography
              sx={{
                mt: 1.5,
                fontWeight: 850,
                color: ADMIN_COLORS.textStrong,
              }}
            >
              {validationTarget.user.email}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeValidationDialog} sx={secondaryButtonSx}>
            Cancelar
          </Button>

          <Button
            onClick={confirmValidationChange}
            disabled={updatingUserId === validationTarget?.user?._id}
            variant="contained"
            sx={{
              ...primaryButtonSx,
              bgcolor:
                validationTarget?.nextValidation === 1
                  ? ADMIN_COLORS.success || "#14A67A"
                  : ADMIN_COLORS.danger,
              background:
                validationTarget?.nextValidation === 1
                  ? ADMIN_COLORS.success || "#14A67A"
                  : ADMIN_COLORS.danger,
              "&:hover": {
                bgcolor:
                  validationTarget?.nextValidation === 1
                    ? "#108A66"
                    : "#B42323",
                background:
                  validationTarget?.nextValidation === 1
                    ? "#108A66"
                    : "#B42323",
              },
            }}
          >
            {updatingUserId === validationTarget?.user?._id
              ? "Guardando..."
              : validationTarget?.nextValidation === 1
                ? "Activar"
                : "Dar de baja"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: "22px",
          },
        }}
      >
        <DialogTitle>Eliminar usuario</DialogTitle>

        <DialogContent>
          <Typography>
            Se eliminará el usuario <strong>{deleteTarget?.email}</strong>. Esta
            acción no se puede deshacer.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={secondaryButtonSx}>
            Cancelar
          </Button>

          <Button onClick={confirmDelete} variant="outlined" sx={dangerButtonSx}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4200}
        onClose={() =>
          setSnackbar((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: "14px",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminPage>
  );
}